import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { useCelebration } from "../../contexts/CelebrationContext";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { FormField, isFieldValid } from "../../components/common/FormField";
import {
  MemberPersonalFields, EMPTY_PERSONAL_FORM, isPersonalFormValid, buildPersonalPayload,
} from "../../components/desk/MemberPersonalFields";
import { MembershipPaymentStep } from "../../components/desk/MembershipPaymentStep";
import { HealthGoalsFields } from "../../components/desk/HealthGoalsFields";

// UX-FD-01 §1 SCR-P02-10: the single most important FD screen — walk-in to paid member, one page,
// 3 steps (no route changes mid-flow, no lost context): personal -> membership & payment ->
// health & goals. `create_member` 409s with MEMBER_EXISTS if the phone is already on file (any
// status, per members.py) — caught here and turned into the re-join-never-duplicates hand-off
// instead of a dead-end error, per the prompt's own explicit requirement.
const PLAN_PAYMENT_TEST_IDS = {
  planSelect: DESK.walkinPlanSelect, startDateInput: DESK.walkinStartDateInput, discountInput: DESK.walkinDiscountInput,
  planNextButton: DESK.walkinPlanNextButton, paymentMode: DESK.walkinPaymentMode, txnRefInput: DESK.walkinTxnRefInput,
  paymentAmount: DESK.walkinPaymentAmount, netPayable: DESK.walkinNetPayable, outstandingBalance: DESK.walkinOutstandingBalance,
  paymentSubmit: DESK.walkinPaymentSubmit, invoiceButton: DESK.walkinInvoiceButton, continueButton: DESK.walkinPaymentContinueButton,
};

export default function WalkInConversion() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const { celebrate } = useCelebration();
  const [step, setStep] = useState("personal");
  const [form, setForm] = useState({ name: "", phone: "" });
  const [personalForm, setPersonalForm] = useState(EMPTY_PERSONAL_FORM);
  const [error, setError] = useState("");
  const [existingMemberId, setExistingMemberId] = useState(null);
  const [member, setMember] = useState(null);
  const [summary, setSummary] = useState(null);

  const detailsValid = isFieldValid("text", true, form.name) && isFieldValid("tel", true, form.phone)
    && isPersonalFormValid(personalForm);

  const createMemberMutation = useMutation({
    mutationFn: () => api.post("/members", { name: form.name, phone: form.phone, branch_id: branchId, ...buildPersonalPayload(personalForm) }),
    onSuccess: (res) => { setMember(res.data); setStep("plan_payment"); },
    onError: async (e) => {
      if (e.response?.status === 409) {
        const found = (await api.get("/members", { params: { phone: form.phone } })).data.members[0];
        setExistingMemberId(found?.id || null);
        setStep("existing");
      } else {
        setError(formatApiError(e).message);
      }
    },
  });

  return (
    <div data-testid={DESK.walkinPage} className="max-w-lg space-y-6">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>New member</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>
          {step === "personal" && "Step 1 of 3 — Personal details"}
          {step === "plan_payment" && "Step 2 of 3 — Membership & payment"}
          {step === "health" && "Step 3 of 3 — Health & goals"}
          {step === "existing" && "Already on file"}
          {step === "done" && "That's it"}
        </p>
      </motion.div>

      {error && <p className="text-sm font-medium" style={{ color: "var(--scarlet-neg)" }} data-testid={DESK.walkinError}>{error}</p>}

      {step === "personal" && (
        <Card>
          <form onSubmit={(e) => { e.preventDefault(); if (!detailsValid) return; setError(""); createMemberMutation.mutate(); }} className="space-y-4">
            <FormField label="Full name" required testId={DESK.walkinNameInput} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <FormField label="Mobile" required type="tel" testId={DESK.walkinPhoneInput} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="9800000000" />
            <MemberPersonalFields form={personalForm} onChange={(k, v) => setPersonalForm((f) => ({ ...f, [k]: v }))} />
            <SubmitButton testId={DESK.walkinNextButton} busy={createMemberMutation.isPending} label="Continue" disabled={!detailsValid} />
          </form>
        </Card>
      )}

      {step === "existing" && (
        <Card testId={DESK.walkinExistingFound}>
          <p className="text-sm" style={{ color: "#141416" }}>
            A member with this phone number already exists. Reactivate their account instead of creating a duplicate.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setStep("personal")}>Back</Button>
            {existingMemberId && (
              <Button
                data-testid={DESK.walkinGoReactivate}
                onClick={() => navigate(`/desk/members/${existingMemberId}?reactivate=1`)}
                className="text-white" style={{ background: "#D6202B" }}
              >
                Go to reactivate
              </Button>
            )}
          </div>
        </Card>
      )}

      {step === "plan_payment" && (
        <Card>
          <p className="text-sm font-semibold mb-3" style={{ color: "#141416" }}>Plan & first payment for {member?.profile?.name}</p>
          <MembershipPaymentStep
            member={member} branchId={branchId} allowSkip={false} testIds={PLAN_PAYMENT_TEST_IDS}
            onComplete={(s) => { setSummary(s); setStep("health"); }}
          />
        </Card>
      )}

      {step === "health" && (
        <Card>
          <p className="text-sm font-semibold mb-3" style={{ color: "#141416" }}>Health & fitness goals</p>
          <HealthGoalsFields member={member} onDone={() => { celebrate("member_added", `${member?.profile?.name} is on the ${summary?.plan?.name} plan.`); setStep("done"); }} />
        </Card>
      )}

      {step === "done" && (
        <Card testId={DESK.walkinDone}>
          <div className="celebrate-pop flex items-center gap-2 mb-2" style={{ color: "var(--scarlet-pos)" }}>
            <Check size={20} /> <p className="font-semibold text-sm">Member added and paid up</p>
          </div>
          <p className="text-sm" style={{ color: "#68686E" }}>
            {member?.profile?.name} is on the {summary?.plan?.name} plan, starting {summary?.membership?.state?.start}.
          </p>
          <Button data-testid={DESK.walkinDoneContinue} onClick={() => navigate(`/desk/members/${member.id}`)} className="mt-4 text-white" style={{ background: "#141416" }}>
            View member
          </Button>
        </Card>
      )}
    </div>
  );
}

function Card({ children, testId }) {
  return <div data-testid={testId} className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>{children}</div>;
}

function SubmitButton({ testId, busy, label, disabled }) {
  return (
    <Button type="submit" data-testid={testId} disabled={busy || disabled} className="w-full text-white" style={{ background: "#D6202B" }}>
      {busy ? <Loader2 size={15} className="animate-spin" /> : label}
    </Button>
  );
}
